"use client";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  sendLoginFormToggled,
  sendPasswordResetFormToggled,
  sendRegisterFormToggled,
} from "@/lib/segment/logistration";
import {
  isPasswordRecoveryPage,
  pathToAuth,
} from "@/app/[locale]/auth/login/components/utils";
import { StartAsPage, SwitchCallbackMouseEvent } from "../components/types";
import refreshUserJwt from "@/lib/auth/refreshUserJwt";
import { useAuth } from "@/components/AuthProvider";

export function useLogistration(
  startAs: StartAsPage,
  disableRedirect?: boolean,
) {
  const searchParams = useSearchParams();
  const nextUrl = searchParams.get("next") || "/";
  const { tpaProviders } = useAuth();

  const { isLoggedIn } = useAuth();

  const processNext = useCallback(async () => {
    try {
      if (isLoggedIn) {
        if (typeof window !== "undefined" && window.newrelic) {
          window.newrelic.addPageAction("useLogistration.processNext", {
            nextUrl,
            startAs,
          });
        }

        if (!disableRedirect) {
          window.location.href = nextUrl;
        }
      }
    } catch (error) {
      // Log auth state checking errors
      if (typeof window !== "undefined" && window.newrelic) {
        window.newrelic.noticeError(error as Error, {
          submodule: "logistration",
          context: "authStateCheck",
          nextUrl: nextUrl,
          startAs: startAs,
          timestamp: new Date().toISOString(),
        });
      }
    }
  }, [nextUrl, startAs, disableRedirect, isLoggedIn]);

  const fetchUserDetailsContext = useCallback(async () => {
    if (tpaProviders?.contextData?.pipelineUserDetails) {
      if (typeof window !== "undefined" && window.newrelic) {
        window.newrelic.addPageAction(
          "useLogistration.fetchUserDetailsContext.pipelineUserDetails",
          { hasPipelineUserDetails: true },
        );
      }
    }
  }, [tpaProviders]);

  useEffect(() => {
    const refreshAndProcess = async () => {
      try {
        await refreshUserJwt();
        processNext();
      } catch (error) {
        console.error("Failed to refresh JWT:", error);
      }
    };
    void refreshAndProcess();
  }, [processNext]);

  useEffect(() => {
    void fetchUserDetailsContext();
  }, [fetchUserDetailsContext]);

  const [currentForm, _setCurrentForm] = useState<StartAsPage>(startAs);
  const [isRegistration, setIsRegistration] = useState(
    startAs === StartAsPage.Register,
  );
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(
    isPasswordRecoveryPage(startAs),
  );
  const [isPasswordRecoveryComplete, setIsPasswordRecoveryComplete] =
    useState(false);

  const setCurrentForm = useCallback((currentForm: StartAsPage) => {
    try {
      setIsRegistration(currentForm === StartAsPage.Register);
      setIsPasswordRecovery(isPasswordRecoveryPage(currentForm));
      _setCurrentForm(currentForm);
    } catch (error) {
      // Log form state errors
      if (typeof window !== "undefined" && window.newrelic) {
        window.newrelic.noticeError(error as Error, {
          submodule: "logistration",
          context: "setCurrentForm",
          attemptedForm: currentForm,
          timestamp: new Date().toISOString(),
        });
      }
    }
  }, []);

  const replaceBase = (path: string) => {
    try {
      const url = new URL(path, window.location.href);
      url.search = window.location.search;
      if (typeof window !== "undefined" && window.newrelic) {
        window.newrelic.addPageAction("useLogistration.replaceBase", {
          url: url.toString(),
        });
      }
      window.history.replaceState(null, "", url);
    } catch (error) {
      // Log navigation errors
      if (typeof window !== "undefined" && window.newrelic) {
        window.newrelic.noticeError(error as Error, {
          submodule: "logistration",
          context: "replaceBase",
          attemptedPath: path,
          currentUrl: window.location.href,
          timestamp: new Date().toISOString(),
        });
      }
    }
  };

  useEffect(() => {
    _setCurrentForm(startAs);
    setIsRegistration(startAs === StartAsPage.Register);
    setIsPasswordRecovery(isPasswordRecoveryPage(startAs));
  }, [startAs]);

  const switchToRegistration = useCallback(
    (e: SwitchCallbackMouseEvent, skipReplaceBase: boolean = false) => {
      try {
        e?.preventDefault();
        const page = StartAsPage.Register;
        if (!skipReplaceBase) {
          replaceBase(pathToAuth(page));
        }
        setCurrentForm(page);
        sendRegisterFormToggled();
      } catch (error) {
        // Log switch errors
        if (typeof window !== "undefined" && window.newrelic) {
          window.newrelic.noticeError(error as Error, {
            submodule: "logistration",
            context: "switchToRegistration",
            timestamp: new Date().toISOString(),
          });
        }
      }
    },
    [setCurrentForm],
  );

  const switchToLogin = useCallback(
    (e: SwitchCallbackMouseEvent, skipReplaceBase: boolean = false) => {
      try {
        e?.preventDefault();
        const page = StartAsPage.Login;
        if (!skipReplaceBase) {
          replaceBase(pathToAuth(page));
        }
        setCurrentForm(page);
        sendLoginFormToggled();
      } catch (error) {
        // Log switch errors
        if (typeof window !== "undefined" && window.newrelic) {
          window.newrelic.noticeError(error as Error, {
            submodule: "logistration",
            context: "switchToLogin",
            timestamp: new Date().toISOString(),
          });
        }
      }
    },
    [setCurrentForm],
  );

  const switchToLoginPasswordRecovery = useCallback(
    (
      e: SwitchCallbackMouseEvent,
      showCompletedDialog: boolean,
      skipReplaceBase: boolean = false,
    ) => {
      try {
        setIsPasswordRecoveryComplete(showCompletedDialog);
        switchToLogin(e, skipReplaceBase);
      } catch (error) {
        // Log password recovery errors
        if (typeof window !== "undefined" && window.newrelic) {
          window.newrelic.noticeError(error as Error, {
            submodule: "logistration",
            context: "switchToLoginPasswordRecovery",
            showCompletedDialog: showCompletedDialog,
            timestamp: new Date().toISOString(),
          });
        }
      }
    },
    [switchToLogin],
  );

  const switchToPasswordRecovery = useCallback(
    (e: SwitchCallbackMouseEvent, skipReplaceBase: boolean = false) => {
      try {
        e?.preventDefault();
        const page = StartAsPage.ForgotPassword;
        if (!skipReplaceBase) {
          replaceBase(pathToAuth(page));
        }
        setCurrentForm(page);
        sendPasswordResetFormToggled();
      } catch (error) {
        // Log password recovery switch errors
        if (typeof window !== "undefined" && window.newrelic) {
          window.newrelic.noticeError(error as Error, {
            submodule: "logistration",
            context: "switchToPasswordRecovery",
            timestamp: new Date().toISOString(),
          });
        }
      }
    },
    [setCurrentForm],
  );

  return {
    currentForm,
    isRegistration,
    isPasswordRecovery,
    isPasswordRecoveryComplete,
    switchToRegistration,
    switchToLogin,
    switchToLoginPasswordRecovery,
    switchToPasswordRecovery,
  };
}
